/**
 * WCC360 v2 — career drill-down prose layer. PRESENTATION ONLY.
 *
 * Sentences live in `src/data/careerCopy.ts` (the single copy library, shared
 * with the derivation script). This file binds each bundle field to the
 * learner key it is compared against, and to the 0..100 scale that mirrors the
 * engine, so a fit marker on this screen can never disagree with the ranking.
 */
import { prefFit, tolFit, type Learner } from "@/lib/scoring";
import { FIELD_COPY, FIELD_LABEL, type FieldName } from "@/data/careerCopy";

export type SignalKind = "pref" | "tol";

export interface SignalSpec {
  /** Bundle field name. */
  field: FieldName;
  /** Short row label. */
  label: string;
  /** Learner key this signal is compared against. */
  learnerKey: keyof Learner;
  kind: SignalKind;
  /** enum value → real sentence. */
  prose: Record<string, string>;
  /** 0..100 numeric position of each enum value, mirroring the engine. */
  scale: Record<string, number>;
}

function spec(
  field: FieldName,
  learnerKey: keyof Learner,
  kind: SignalKind,
  scale: Record<string, number>,
): SignalSpec {
  return { field, label: FIELD_LABEL[field], learnerKey, kind, prose: FIELD_COPY[field], scale };
}

/* 3 · A day in this work — the nine Context_* fields */

export const DAY_SIGNALS: SignalSpec[] = [
  spec("Context_IndoorOutdoor", "prefOutdoor", "pref", { Mostly_Indoor: 0, Mixed: 50, Mostly_Outdoor: 100 }),
  spec("Context_MovementLevel", "prefMovement", "pref", { Mostly_Sitting: 0, Mixed: 50, On_the_Move: 100 }),
  spec("Context_ScreenTime", "prefScreenTime", "pref", { Low: 0, Medium: 50, High: 100 }),
  spec("Context_PeopleContactLevel", "tolPeopleContact", "tol", { Low: 0, Medium: 40, High: 75, High_Distress: 100 }),
  spec("Context_TravelIntensity", "prefTravel", "pref", { None: 0, Low: 25, Occasional: 50, Medium: 50, Frequent: 100 }),
  spec("Context_SchedulePattern", "prefStableSchedule", "pref", { Intense_Irregular: 0, Variable: 50, Predictable: 100 }),
  spec("Context_SensoryExposure", "tolSensory", "tol", { No_Special_Exposure: 0, Smell_Noise: 40, Hazardous_Materials: 70, Body_Fluids: 100 }),
  spec("Context_PublicSpeakingDemand", "tolPublicSpeaking", "tol", { Low: 0, Medium: 50, High: 100 }),
  spec("Context_ClosePhysicalWork", "tolClosePhysical", "tol", { No: 0, Yes: 100 }),
];

/* 4 · What it asks of your energy */

export const ENERGY_SIGNALS: SignalSpec[] = [
  spec("Energy_SocialLoad", "tolSocialLoad", "tol", { Low: 0, Medium: 50, High: 100 }),
  spec("Energy_RecoveryDifficulty", "tolEmotionalLoad", "tol", { Low: 0, Medium: 50, High: 100 }),
  spec("Energy_CognitiveMode", "prefFocusVsSwitching", "pref", { Deep_Focus: 0, Mixed: 50, Context_Switching: 100 }),
  spec("Energy_CollaborationStyle", "prefSoloVsTeam", "pref", { Solo: 0, Mixed: 50, Collaborative: 100 }),
  spec("GruntWork_Intensity", "tolGruntWork", "tol", { Low: 0, Medium: 50, High: 100 }),
];

/* 5 · What it pays, and what it protects */

export const VALUE_SIGNALS: SignalSpec[] = [
  spec("Value_Autonomy", "prefAutonomy", "pref", { Low: 0, Medium: 50, High: 100 }),
  spec("Value_ImpactVisibility", "prefImpactVisibility", "pref", { Low: 0, Medium: 50, High: 100 }),
  spec("Value_IncomeUpside", "prefIncomeUpside", "pref", { Low: 0, Medium: 50, High: 100 }),
  spec("Value_Prestige", "prefPrestige", "pref", { Low: 0, Medium: 50, High: 100 }),
  spec("Value_Stability", "prefStability", "pref", { Low: 0, Medium: 50, High: 100 }),
  spec("Value_Creativity", "prefCreativity", "pref", { Low: 0, Medium: 50, High: 100 }),
];

/* ------------------------------------------------------------------ */
/* Fit markers                                                         */
/* ------------------------------------------------------------------ */

export type FitLevel = "fit" | "near" | "stretch";

export interface SignalRow {
  label: string;
  sentence: string;
  /** null when there's no assessment to compare against. */
  fit: FitLevel | null;
  fitScore: number | null;
}

export const FIT_COPY: Record<FitLevel, string> = {
  fit: "Fits you",
  near: "Close enough",
  stretch: "A stretch for you",
};

export const FIT_CLASS: Record<FitLevel, string> = {
  fit: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  near: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  stretch: "text-amber-400 border-amber-500/30 bg-amber-500/10",
};

function level(score: number): FitLevel {
  if (score >= 75) return "fit";
  if (score >= 55) return "near";
  return "stretch";
}

/**
 * Build display rows for a signal group. Fields with no value in the bundle
 * are dropped entirely — we never print "unknown", and never a raw enum.
 */
export function buildSignalRows(
  specs: SignalSpec[],
  fields: Record<string, string | null | undefined>,
  learner: Learner | null,
): SignalRow[] {
  const out: SignalRow[] = [];
  for (const s of specs) {
    const raw = fields[s.field];
    if (!raw) continue;
    const sentence = s.prose[raw];
    if (!sentence) continue; // unknown enum → say nothing rather than leak it
    let fit: FitLevel | null = null;
    let fitScore: number | null = null;
    if (learner) {
      const careerVal = s.scale[raw];
      const learnerVal = learner[s.learnerKey];
      if (typeof careerVal === "number" && typeof learnerVal === "number") {
        fitScore = Math.round(
          s.kind === "pref" ? prefFit(learnerVal, careerVal) : tolFit(learnerVal, careerVal),
        );
        fit = level(fitScore);
      }
    }
    out.push({ label: s.label, sentence, fit, fitScore });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Market copy                                                         */
/* ------------------------------------------------------------------ */

export const DEMAND_COPY: Record<string, string> = {
  "Very High": "Demand for this work is expected to keep growing well past 2040.",
  High: "Demand looks strong through 2040.",
  "High to Very High": "Demand looks strong through 2040, and possibly stronger.",
  "Medium-High": "Demand looks solid through 2040.",
  Medium: "Demand looks steady through 2040 — neither shrinking nor booming.",
  Stable: "This work is expected to hold steady through 2040.",
  Low: "Demand for this work is expected to shrink. Worth going in with your eyes open.",
};

export function automationCopy(pct: number): string {
  if (pct >= 70)
    return `Around ${pct}% of today's tasks in this role could be automated. The role probably survives; the daily work changes a lot.`;
  if (pct >= 40)
    return `Around ${pct}% of today's tasks could be automated. Expect the tools to change faster than the job title does.`;
  return `Only about ${pct}% of today's tasks look automatable. The human part of this work is hard to replace.`;
}

export function marketSourceCopy(
  market: string,
  stale: boolean | undefined,
  observations: number | null | undefined,
): string | null {
  if (market === "unpriced")
    return "We have no salary evidence for this role yet. We'd rather tell you that than show you a number we made up.";
  if (market === "live") {
    const obs = observations && observations > 0 ? ` from ${observations} recent postings` : "";
    return stale
      ? `Salary read from live postings${obs}, last refreshed over a day ago.`
      : `Salary read from live postings${obs}.`;
  }
  if (market === "seed" || market === "fallback")
    return "Salary is an estimate from our reference data, not live postings. Treat it as a band, not a quote.";
  return null;
}
